import { RequestAccessModal } from '@app/components/account/RequestAccessModal';
import { CreateAccountModal } from '@app/components/accounts/CreateAccount/CreateAccountModal';
import { useLookbookContext } from '@app/components/lookbook/context/LookbookContext';
import { LookbookDetailsModalContextProvider } from '@app/components/lookbook/context/LookbookDetailsModalContext';
import { useLookbookWishlistContext } from '@app/components/lookbook/context/LookbookWishlistContext';
import { CreateSelectionFromWishlistModal } from '@app/components/lookbook/CreateSelectionFromWishlistModal';
import { WishlistShareModal } from '@app/components/lookbook/WishlistShareModal';
import { LookbookProductCard } from '@app/components/products/LookbookProductsList/LookbookProductCard';
import { ProductCardSkeleton } from '@app/components/products/ProductCard/ProductCardSkeleton';
import { LookbookProductDetailsModal } from '@app/components/products/ProductDetails/LookbookProductDetailsModal';
import { Button } from '@app/components/various/Button';
import { EmptyState } from '@app/components/various/EmptyState';
import { Account, Remove, Share } from '@app/components/various/Icons';
import { PageTitle } from '@app/components/various/PageTitle';
import { SkeletonLayout } from '@app/components/various/SkeletonLayout';
import { BREAKPOINTS } from '@app/constants/breakpoints';
import { getIsAccountEditingEnabled, getIsBuyer, getIsLoggedIn, getIsSeller, getUser } from '@app/ducks';
import { getDeliveryWindowIdsFromVariants } from '@app/logic/deliveryWindows';
import { getHasPermissions } from '@app/logic/User';
import { compiledPaths } from '@app/paths';
import { useLookbookProductsByIds } from '@app/services/hooks/lookbook/useLookbookProductsByIds';
import { cn } from '@app/utils/cn';
import { useIsScreenSize } from '@app/utils/hooks';
import { useModalToggle } from '@app/utils/hooks/useModalToggle';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

interface Props {
  productId: string | undefined;
  variantId: string | undefined;
}
export const LookbookWishlistPageContent = ({ productId, variantId }: Props) => {
  const { t } = useTranslation(['lookbook', 'common', 'accounts', 'linesheets', 'selections']);
  const isLoggedIn = useSelector(getIsLoggedIn);
  const user = useSelector(getUser);
  const { lookbookId } = useLookbookContext();
  const { wishlist, wishlistArray, clearWishlist } = useLookbookWishlistContext();
  const hasUserCreateAccountPermissions = isLoggedIn && getHasPermissions(user, 'CREATE_ACCOUNT');
  const isAccountEditingEnabled = useSelector(getIsAccountEditingEnabled);
  const isBuyer = useSelector(getIsBuyer);
  const isSeller = useSelector(getIsSeller);
  const isMobile = useIsScreenSize(BREAKPOINTS.SM);
  const { data: wishlistProductsData, isLoading } = useLookbookProductsByIds({ lookbookId, products: [...wishlist] });
  const products = React.useMemo(() => wishlistProductsData ?? [], [wishlistProductsData]);
  const { showModal: showRequestAccountModal, isOpen: showRequestAccountModalOpen, hideModal: hideRequestAccountModal } = useModalToggle();
  const { showModal: showCreateAccountModal, isOpen: showCreateAccountModalOpen, hideModal: hideCreateAccountModal } = useModalToggle();
  const { showModal: showShareWishlistModal, isOpen: showShareWishlistModalOpen, hideModal: hideShareWishlistModal } = useModalToggle();
  const {
    showModal: showCopyToNewSelectionModal,
    isOpen: isCopyToNewSelectionModalOpen,
    hideModal: hideCopyToNewSelectionModal,
  } = useModalToggle();

  const isGuest = !isSeller && !isBuyer;
  const canCreateAccount = isSeller && hasUserCreateAccountPermissions && isAccountEditingEnabled;
  const hasItems = wishlist.size !== 0;
  const primaryActionButtonSize = isMobile ? 'medium' : 'large';
  const deliveryWindows = React.useMemo(() => getDeliveryWindowIdsFromVariants(products), [products]);

  return (
    <PageTitle title={t('lookbook:wishlist')}>
      <div className="mx-auto w-full max-w-screen-xl px-4 lg:px-8">
        <div
          className={cn('flex items-center justify-between pb-8 pt-2 sm:pt-4 lg:pt-8', {
            'border-b border-neutral-90': hasItems,
          })}
        >
          <div className="flex items-center gap-2">
            <h2 className="heading-2 sm:heading-1">{t('lookbook:wishlist')}</h2>
          </div>
          <div className="flex gap-2">
            {!isBuyer && (
              <Button size={primaryActionButtonSize} color="secondary" onClick={showShareWishlistModal} icon={Share}>
                {!isMobile && t('lookbook:share_wishlist')}
              </Button>
            )}

            {canCreateAccount && (
              <Button size={primaryActionButtonSize} onClick={showCreateAccountModal} icon={Account}>
                {t('accounts:create_account')}
              </Button>
            )}

            {isBuyer && (
              <Button size={primaryActionButtonSize} onClick={showShareWishlistModal} icon={Share}>
                {!isMobile && t('lookbook:share_wishlist')}
              </Button>
            )}
            {isGuest && (
              <Button size={primaryActionButtonSize} onClick={showRequestAccountModal} icon={Account}>
                {t('common:request_account')}
              </Button>
            )}
          </div>
        </div>
        {!hasItems ?
          <EmptyState title={t('lookbook:empty_wishlist.title')} subtitle={t('lookbook:empty_wishlist.subtitle')}>
            <Button as={Link} to={compiledPaths.LOOKBOOK_SINGLE({ lookbookId })} color="secondary" variant="bordered">
              {t('lookbook:select_products')}
            </Button>
          </EmptyState>
        : <>
            <div className="mx-auto flex items-center justify-between pb-3 pt-6">
              <div className="flex items-center gap-2 sm:gap-6">
                <h2 className="heading-2">{t('lookbook:wishlist_products_header', { count: wishlist.size })}</h2>
                {wishlist.size > 0 && (
                  <Button size="medium" variant="bordered" color="secondary" icon={isMobile ? Remove : undefined} onClick={clearWishlist}>
                    {!isMobile && t('common:remove_all')}
                  </Button>
                )}
              </div>
              {!isGuest && (
                <div className="flex items-center gap-2 sm:gap-6">
                  <Button size="medium" variant="bordered" color="secondary" onClick={showCopyToNewSelectionModal}>
                    {t('selections:copy_to_new_selection')}
                  </Button>
                </div>
              )}
            </div>
            <div className="mr-0 grid grid-cols-[repeat(auto-fill,minmax(10.5rem,1fr))] gap-x-3 gap-y-3 pb-8 pt-8 md:grid-cols-[repeat(auto-fill,minmax(13.5rem,1fr))]">
              <SkeletonLayout skeleton={<ProductCardSkeleton />} repeatLayout={4} isLoading={isLoading}>
                {products.map(product => (
                  <LookbookProductCard key={product.product} gridSize="medium" productInfo={product} />
                ))}
              </SkeletonLayout>
            </div>
          </>
        }
      </div>
      <LookbookDetailsModalContextProvider lookbookId={lookbookId} productId={productId} variantId={variantId} products={products}>
        <LookbookProductDetailsModal />
      </LookbookDetailsModalContextProvider>
      {isGuest && (
        <RequestAccessModal
          isOpen={showRequestAccountModalOpen}
          onSuccess={hideRequestAccountModal}
          onClose={hideRequestAccountModal}
          lookbookId={lookbookId}
          wishlist={wishlistArray}
        />
      )}
      <WishlistShareModal isOpen={showShareWishlistModalOpen} onClose={hideShareWishlistModal} />
      {canCreateAccount && (
        <CreateAccountModal
          isOpen={showCreateAccountModalOpen}
          onClose={hideCreateAccountModal}
          onSuccess={hideCreateAccountModal}
          shouldCreateSelection
          products={wishlistArray}
          deliveryWindows={deliveryWindows}
          createSource="lookbook"
        />
      )}
      <CreateSelectionFromWishlistModal
        isOpen={isCopyToNewSelectionModalOpen}
        onClose={hideCopyToNewSelectionModal}
        products={wishlistArray}
        deliveryWindows={deliveryWindows}
      />
    </PageTitle>
  );
};
